import debugFn from 'debug';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { areListViewParamsAtDefaults } from '../helpers/are-list-view-params-at-defaults';
import { isCleanRoute } from '../helpers/is-clean-route';
import { StoredListViewState, useListViewStateStore } from '../stores/list-view-state.store';
import { FilterDefinition, ListViewFilters, ListViewSort } from '../types';

const debug = debugFn('useStickyListViewState');

/**
 * Parameters structure that matches what comes from useQueryStates.
 * Includes filters, pageIndex, pageSize, and sort.
 */
type AllParams = {
  pageIndex: number;
  pageSize: number;
  sort: ListViewSort | null;
} & ListViewFilters;

/** Subset of nuqs setState options used when writing sticky values into the URL. */
type SetAllParamsOptions = {
  clearOnDefault?: boolean;
};

type SetAllParams = (
  params: Partial<AllParams> | ((old: AllParams) => Partial<AllParams>),
  options?: SetAllParamsOptions,
) => void;

/**
 * Return type for useStickyListViewState hook.
 */
export interface UseStickyListViewStateReturn {
  /**
   * Clear stored state for this listId.
   * Used by resetFilters to ensure next visit to clean route won't restore old filters.
   */
  clearStoredState: () => void;
}

/**
 * Applies stored sticky state to the URL, or mirrors a dirty URL into storage.
 * @returns true when URL was hydrated from storage
 */
function hydrateOrSyncListId(
  listId: string,
  allParams: AllParams,
  setAllParams: SetAllParams,
  getStoredState: (listId: string) => StoredListViewState | null,
  setStoredState: (listId: string, state: StoredListViewState) => void,
  consumeStickySeed?: () => void,
): boolean {
  // Empty URL means storage wins; params already in the URL take precedence.
  if (isCleanRoute()) {
    const storedState = getStoredState(listId);
    debug('Checking stored state for listId: %s', listId, { storedState });

    if (storedState) {
      debug('Hydrating URL from stored state', { listId, storedState });
      // Force sticky values into the URL even when they equal seeded parser defaults.
      // Otherwise clearOnDefault strips them, the route stays clean, and clear(null)
      // is a no-op against the seeded default.
      setAllParams(
        {
          ...storedState.filters,
          pageIndex: storedState.pageIndex,
          pageSize: storedState.pageSize,
          sort: storedState.sort,
        },
        { clearOnDefault: false },
      );
      consumeStickySeed?.();
      return true;
    }

    debug('No stored state found, using defaults');
    return false;
  }

  const { pageIndex, pageSize, sort, ...urlFilters } = allParams;
  const stateToStore: StoredListViewState = {
    filters: urlFilters,
    sort: sort ?? null,
    pageIndex,
    pageSize,
  };
  debug('URL has params, syncing to storage', { listId, stateToStore });
  setStoredState(listId, stateToStore);
  return false;
}

/**
 * Hook that manages sticky ListView state persistence.
 * Handles hydration from storage on clean routes and syncing URL changes to storage.
 *
 * @param listId - Unique identifier for this ListView instance
 * @param allParams - Current URL parameters from useQueryStates
 * @param setAllParams - Function to update URL parameters
 * @param defaultPageSize - Default page size for clean route detection
 * @param filters - Filter definitions to extract filter keys
 * @param enabled - When false, skips hydration and persistence (URL defaults only)
 * @param consumeStickySeed - Drops one-shot parser seeding after sticky values are written to the URL
 * @returns Object with clearStoredState function
 */
export function useStickyListViewState(
  listId: string,
  allParams: AllParams,
  setAllParams: SetAllParams,
  defaultPageSize: number,
  filters?: FilterDefinition[],
  enabled = true,
  consumeStickySeed?: () => void,
): UseStickyListViewStateReturn {
  const getStoredState = useListViewStateStore((state) => state.getStoredState);
  const setStoredState = useListViewStateStore((state) => state.setStoredState);
  const clearStoredState = useListViewStateStore((state) => state.clearStoredState);

  const hasHydratedRef = useRef(false);
  const isInitialMountRef = useRef(true);
  const activeListIdRef = useRef(listId);
  const allParamsRef = useRef(allParams);
  allParamsRef.current = allParams;
  const consumeStickySeedRef = useRef(consumeStickySeed);
  consumeStickySeedRef.current = consumeStickySeed;

  // Hydrate before paint (seeded parsers cover first paint; this writes the URL).
  // Also re-runs when listId changes without a remount.
  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const listIdChanged = activeListIdRef.current !== listId;
    const isMount = isInitialMountRef.current;

    if (!isMount && !listIdChanged) {
      return;
    }

    isInitialMountRef.current = false;
    activeListIdRef.current = listId;

    debug(
      'Hydrate/sync for listId: %s (mount=%s, listIdChanged=%s)',
      listId,
      isMount,
      listIdChanged,
    );

    const didHydrate = hydrateOrSyncListId(
      listId,
      allParamsRef.current,
      setAllParams,
      getStoredState,
      setStoredState,
      consumeStickySeedRef.current,
    );
    if (didHydrate) {
      hasHydratedRef.current = true;
      debug('Hydration complete, hasHydratedRef set to true');
    }
    // allParams read via ref so filter edits do not re-trigger hydration;
    // effect is intentionally keyed by listId/enabled only
  }, [listId, enabled, getStoredState, setAllParams, setStoredState]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (isInitialMountRef.current || hasHydratedRef.current) {
      debug('Skipping sync (initial mount or just hydrated)', {
        isInitialMount: isInitialMountRef.current,
        hasHydrated: hasHydratedRef.current,
      });
      hasHydratedRef.current = false;
      return;
    }

    // Chip nav clears the query string and params snap to defaults. Skip persist only in
    // that case so a user returning filters to defaults on a dirty URL still saves.
    if (isCleanRoute() && areListViewParamsAtDefaults(allParams, filters, defaultPageSize)) {
      debug('Skipping sync on clean route at default params', { listId });
      return;
    }

    const { pageIndex, pageSize, sort, ...urlFilters } = allParams;
    const stateToStore: StoredListViewState = {
      filters: urlFilters,
      sort: sort ?? null,
      pageIndex,
      pageSize,
    };

    debug('Syncing URL changes to storage', {
      listId,
      stateToStore,
      allParams,
    });

    setStoredState(listId, stateToStore);
  }, [allParams, enabled, listId, filters, defaultPageSize, setStoredState]);

  return {
    clearStoredState: () => {
      debug('Clearing stored state for listId: %s', listId);
      clearStoredState(listId);
    },
  };
}
